<template>
  <div class="calender-day-body px-10 py-10 leading-relaxed">
    <!-- 长期负荷短期负荷负荷比 -->
    <template v-if="showXti && stageInfo.showTrainSum">
      <div
        v-for="(trainsum, tIndex) in TRAIN_SUM"
        :key="tIndex"
        class="text-12 font-medium mb-5"
        :style="{ color: trainsum.color }"
      >
        <span>{{ $t(trainsum.label) }}: </span>
        <span>{{ getTrainingSumLabel(trainsum, stageInfo) }}</span>
      </div>
    </template>
    <!-- 运动时间/距离/负荷 -->
    <div
      v-for="(detail, dIndex) in TRAIN_SUM_DETAIL"
      :key="dIndex"
    >
      <div class="text-12 font-medium mb-5">
        <div class="text-text-3">
          {{ $t(detail.label) }}
        </div>
        <div>
          <template v-if="stageInfo.showActData">
            <span class="text-fill-1">
              {{ stageInfo.trainSum[detail.act] }}
            </span>
            /
          </template>
          <span class="text-text-3">{{ stageInfo.trainSum[detail.plan] }} </span>
        </div>
      </div>
    </div>
    <!-- 运动类型统计 -->
    <template v-if="isShowTypeCount(stageInfo.sumByType)">
      <div
        v-for="sumItem in stageInfo.sumByType"
        :key="sumItem.sportType"
      >
        <!-- 图标&名称 -->
        <div class="flex flex-row justify-start items-center">
          <div class="sport-type-icon mr-8">
            <SportIcon
              class="text-20"
              :sport-type="sumItem.sportType"
              :map-type="sumItem.bitmapType"
            ></SportIcon>
          </div>
          <!-- 运动分类名称 -->
          <div class="text-white">{{ getSportName(sumItem.sportType) }}</div>
        </div>
        <!-- 分类统计明细 -->

        <!-- 运动时间 -->
        <div
          class="text-12 mb-5"
          v-if="parseInt(sumItem.trainSum.actualDuration) || parseInt(sumItem.trainSum.planDuration)"
        >
          <div class="text-text-3">
            {{ $t('R3010') }}
          </div>
          <div class="text-text-3">
            <span class="text-white">{{ s2hmsFullStr(sumItem.trainSum.actualDuration) }}</span>
            <span>{{ ` / ${s2hmsFullStr(sumItem.trainSum.planDuration)}` }}</span>
          </div>
        </div>
        <!-- 运动距离 -->
        <div
          class="text-12 mb-5"
          v-if="parseInt(sumItem.trainSum.actualDistance) || parseInt(sumItem.trainSum.planDistance)"
        >
          <div class="text-text-3">
            {{ $t('R3011') }}
          </div>
          <div class="text-text-3">
            <span class="text-white">
              {{ distanceKM2mile(parseInt(sumItem.trainSum.actualDistance) / 100000, 2, currentUnit) }}
              {{ currentUnit === 0 ? ' km' : ' mi' }}
            </span>
            <span>
              {{ ` / ${distanceKM2mile(parseInt(sumItem.trainSum.planDistance) / 100000, 2, currentUnit)}` }}
              {{ currentUnit === 0 ? ' km' : ' mi' }}
            </span>
          </div>
        </div>
        <!-- 训练负荷 -->
        <div
          class="text-12 mb-5"
          v-if="parseInt(sumItem.trainSum.actualTrainingLoad) || parseInt(sumItem.trainSum.planTrainingLoad)"
        >
          <div class="text-text-3">
            {{ $t('R3009') }}
          </div>
          <div class="text-text-3">
            <span class="text-white"> {{ sumItem.trainSum.actualTrainingLoad }} TL </span>
            <span>
              {{ sumItem.trainSum.planTrainingLoad ? ` / ${sumItem.trainSum.planTrainingLoad} TL` : `` }}
            </span>
          </div>
        </div>
        <!-- 累计爬升，室内攀岩或者抱石不显示该项 -->
        <div
          v-if="
            !isIndoorClimbOrBoulderingTrainingSportType(sumItem.sportType) &&
            (parseInt(sumItem.trainSum.actualElevGain) || parseInt(sumItem.trainSum.planElevGain))
          "
          class="text-12 mb-5"
        >
          <div class="text-text-3">
            {{ $t('R9027') }}
          </div>
          <div class="text-text-3">
            <span class="text-white"> {{ parseElevGain(sumItem.trainSum.actualElevGain) }} {{ visibleElevGainUnit }}</span>
            <span> {{ ` / ${parseElevGain(sumItem.trainSum.planElevGain)}` }} {{ visibleElevGainUnit }} </span>
          </div>
        </div>
        <!-- 路线 -->
        <div
          v-if="
            isIndoorClimbOrBoulderingTrainingSportType(sumItem.sportType) && (sumItem.trainSum.actualPitch || sumItem.trainSum.planPitch)
          "
          class="text-12 mb-5"
        >
          <div class="text-text-3">
            {{ $t(sumItem.sportType === 7 ? 'H9095' : 'H9039') }}
          </div>
          <div class="text-text-3">
            <span class="text-white"> {{ sumItem.trainSum.actualPitch }} {{ $t(sumItem.sportType === 7 ? 'H9095' : 'H9039') }}</span>
            <span> {{ ` / ${sumItem.trainSum.planPitch}` }} {{ $t(sumItem.sportType === 7 ? 'H9095' : 'H9039') }} </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script setup>
import { computed } from 'vue';
import { TRAIN_SUM, TRAIN_SUM_DETAIL } from '@client/pages/personal/schedule/calender/instance';
import Definition from '@client/config/definition';
import SportIcon from '@client/components/sportIcon/index.vue';
import { unit as mathjsUnit } from 'mathjs';
import { s2hmsFullStr } from '@client/helper/day';
import { distanceKM2mile } from '@client/helper/distance';
import { isNoValue } from '@client/helper/validate';
import { useI18n } from 'vue-i18n';
import { userStore as useUserStore } from '@client/store/userStore';
import * as corosSportConfig from '@coros/sport-config';

const { isIndoorClimbOrBoulderingTrainingSportType } = corosSportConfig.sport.climb;

const { t } = useI18n();
const props = defineProps({
  stageInfo: {
    type: Object,
    default() {
      return {};
    }
  },
  from: {
    type: String,
    default: 'schedule'
  }
});

const userStore = useUserStore();
const currentUnit = computed(() => {
  const defaultUnit = userStore.unit;
  return defaultUnit;
});
const getTrainingSumLabel = (trainsum, stageInfo) => {
  const value = stageInfo.trainSum[trainsum.key];
  if ([undefined, null, ''].includes(value)) return '--';
  if (trainsum.key === 'actualTrainingLoadRatio') {
    return Math.round(value * 100) + '%';
  } else {
    return value;
  }
};

// 显示各种负荷
const showXti = computed(() => {
  return props.from === 'schedule';
});
const visibleElevGainUnit = computed(() => {
  return currentUnit.value === 0 ? 'm' : 'ft';
});
const parseElevGain = (elevGain) => {
  return mathjsUnit(elevGain, 'cm').toNumber(visibleElevGainUnit.value).toFixed(0);
};
const isShowTypeCount = (sumByType = []) => {
  let count = 0;
  if (sumByType.length > 0) {
    sumByType.forEach((s) => {
      let littleCount = 0;
      for (const i in s.trainSum) {
        count += Number(s.trainSum[i]);
        littleCount += Number(s.trainSum[i]);
      }
      s.count = littleCount;
    });
    return count > 0;
  } else {
    return false;
  }
};
const getSportName = (sportType) => {
  const key = Definition.getSportCategoryByValue(sportType)
    ? Definition.getSportCategoryByValue(sportType).i18n
    : Definition.getSportI18nKeyBySportType(sportType);
  return t(key);
};
</script>
